import { CourseAPIResponse } from 'app/types/courses';
import { useUser } from 'core/lite/hooks/useUser';
import * as coreEntities from 'core/store/index';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

/**
 * Whether AI features should be enabled for the current user (TANAI-1797). The condition
 * differs by role:
 *  - Teacher (and any non-student AI account): an AI-enabled account is enough.
 *  - Student: an AI-enabled account AND at least one course where has_tangerine_ai === 1.
 *
 * Courses are fetched only once the user is the student candidate, so pre-login/teacher
 * routes never trigger a courses request (which 403s and crashes the courses saga when
 * unauthenticated).
 */
export function useIsAiEligible(): boolean {
  const dispatch = useDispatch();
  const { user, isStudent, isTangerineAi } = useUser();
  const hasAiAccount = Boolean(user?.guid) && isTangerineAi;
  const isStudentCandidate = hasAiAccount && isStudent;

  const courses = useSelector((state: unknown) => coreEntities.courses.selectors.getCourses(state)) as CourseAPIResponse[] | null;

  useEffect(() => {
    if (isStudentCandidate && !courses) {
      dispatch(coreEntities.courses.actions.getCourses({ params: { offset: 0, pageSize: 1000 } }));
    }
  }, [isStudentCandidate, courses, dispatch]);

  if (!hasAiAccount) {
    return false;
  }

  // Teachers (and any non-student AI account) only need the account flag.
  if (!isStudent) {
    return true;
  }

  // Students additionally require at least one AI-enabled course.
  for (const course of courses ?? []) {
    if (course?.has_tangerine_ai === 1) {
      return true;
    }
  }

  return false;
}

export default useIsAiEligible;
